import { Stack, Text, useMantineTheme } from '@mantine/core'
import { useMediaQuery } from '@mantine/hooks'
import { IconExclamationCircle } from '@tabler/icons-react'
import React, { useCallback, useEffect, useState } from 'react'
import { Trans, useTranslation } from 'react-i18next'
import { normalize } from 'viem/ens'

import { useChainFunctions } from '@/hooks/useChain'
import useClaimFreeHandle from '@/hooks/useClaimFreeHandle'
import useLocalStorage from '@/hooks/useLocalStorage'
import { l1IdSubnetWagmiConfig } from '@/plugins/auth/config'

import { CONTRACT_CONTROLLER_ADDRESS } from '../../../constants/blockchain'
import L1Controller from '../../../contracts/L1NSController'
import { UserIdentity } from '../../../utils/utils'
import RootButton from '../../Buttons/RootButton'
import ErrorMessage from '../../ErrorMessage'
import { ConfirmationLoading, ConfirmationReady } from '../../Identity/ConfirmModals/utils'
import { AtIcon } from '../../SpecialInputText'
import classes from './ConfirmUsername.module.css'

interface ConfirmFreeUsernameSetupProps {
  close: () => void
  success: () => void
  address: string
  handle: string
  handleConfirmation: (confirmed: boolean) => void
}

const ConfirmFreeUsername: React.FC<ConfirmFreeUsernameSetupProps> = ({
  close,
  success,
  address,
  handle,
  handleConfirmation,
}) => {
  const { t } = useTranslation()
  const { writeContract } = useChainFunctions(l1IdSubnetWagmiConfig)
  const theme = useMantineTheme()

  const [isLoading, setIsLoading] = useState(false)
  const isMobile = useMediaQuery(`(max-width: ${theme.breakpoints.sm})`)
  const [isReady, setIsReady] = useState(false)
  const [errorMessage, setErrorMessage] = useState<string | JSX.Element>('')
  const [loadingNewCode, setLoadingNewCode] = useState(false)
  const [dnsErrorMessage, setDnsErrorMessage] = useState<JSX.Element | string>('')
  const [userIdentity, setUserIdentity] = useLocalStorage<UserIdentity>(`userIdentity-${address}`)

  // Normalize handle
  const normalizedHandle = normalize(handle)

  useEffect(() => {
    // check if handle complies with DNS rules
    setDnsErrorMessage('')
    const dnsRegex =
      /^(?=.{1,253}$)(?!-)[A-Za-z0-9-]{1,63}(?!\.)(?:\.(?!-)[A-Za-z0-9-]{1,63}(?!\.))*$/
    const isValid = dnsRegex.test(handle)
    if (!isValid) {
      setDnsErrorMessage(
        <Text style={{ color: theme.colors.notification[0] }}>
          <Trans i18nKey="components.identity.confirmModal.dnsError">
            This username may not be DNS compatible. This could limit the interoperability of your
            metaverse identity in the future. For more info, see{' '}
            <a
              href="https://docs.lamina1.com/docs/L1-Username-Guidelines--Recommendations"
              target="_blank"
              rel="noreferrer"
            >
              L1 Usernames Guidelines & Recommendations
            </a>
          </Trans>
        </Text>
      )
    }
  }, [handle])

  const {
    retval: { fullHandle, nonceHex, code },
    refetch,
    errorPow,
  } = useClaimFreeHandle(address, normalizedHandle)

  const newCode = useCallback(() => {
    setLoadingNewCode(true)
    refetch().then(() => setLoadingNewCode(false))
  }, [refetch])

  const claimHandle = useCallback(async () => {
    setErrorMessage('')
    if (!userIdentity) return
    setIsLoading(true)
    handleConfirmation(true)
    try {
      // Register free username
      await writeContract({
        address: CONTRACT_CONTROLLER_ADDRESS,
        abi: L1Controller,
        functionName: 'registerFree',
        args: [fullHandle, nonceHex],
      })

      setIsLoading(false)
      setIsReady(true)
      setUserIdentity({
        ...userIdentity,
        username: fullHandle.split('.')[0],
      })
    } catch (error: any) {
      setIsLoading(false)
      setIsReady(false)
      handleConfirmation(false)
      setErrorMessage(error.message)
    }
  }, [userIdentity, fullHandle, nonceHex, code, errorPow])

  return (
    <Stack className={classes.identityComponent} style={{ gap: '20px' }}>
      {errorMessage && !isLoading && (
        <ErrorMessage
          message={t('components.identity.error.freeUsername', 'Error claiming free username')}
          details={errorMessage}
        />
      )}
      {errorPow && !isLoading && (
        <ErrorMessage
          message={t('components.identity.error.pow', 'Error executing PoW')}
          details={errorPow}
        />
      )}
      {isLoading && (
        <ConfirmationLoading title={t('components.identity.loadingId', 'L1 to ID Subnet')} />
      )}
      {isReady && (
        <ConfirmationReady
          message={t('components.identity.usernameClaimed', 'Username claimed!')}
          success={success}
        />
      )}
      {!isReady && !errorPow && !isLoading && (
        <>
          <Text>
            <Trans i18nKey="components.identity.confirmModal.bodyFree">
              Please confirm this is the username you’d like to use for your LAMINA1 Hub account.
              You can change this username via the Profile Page.
            </Trans>
          </Text>
          <Stack className={classes.confirmUsername}>
            <Stack className={classes.identityDisplay}>
              <Text className={classes.atIcon}>
                <AtIcon />
              </Text>
              <Text className={classes.identityText}>{normalizedHandle}</Text>
              {normalizedHandle.length < 26 && (
                <Text className={classes.identityText}>
                  {!code || loadingNewCode ? '#PoW' : `${code}`}
                </Text>
              )}
            </Stack>
            {normalizedHandle.length >= 26 && (
              <Text className={classes.identityText}>
                {!code || loadingNewCode ? '#PoW' : `${code}`}
              </Text>
            )}
          </Stack>
          {dnsErrorMessage && (
            <Stack className={classes.dnsErrorMessageComponent} style={{ gap: '6px' }}>
              <IconExclamationCircle className={classes.dnsIcon} />
              <Text className={classes.dnsErrorMessage}>{dnsErrorMessage}</Text>
            </Stack>
          )}
          <Stack className={classes.identityButtonsStack} style={{ gap: '8px' }}>
            {!isMobile && (
              <RootButton expand secondary style1={false} onClick={close}>
                {t('buttons.cancel', 'Cancel')}
              </RootButton>
            )}
            <RootButton
              expand
              secondary
              onClick={newCode}
              style1={false}
              disabled={!code || loadingNewCode}
            >
              {t('components.identity.confirmModal.orderNewCode', 'New Code')}
            </RootButton>
            {errorMessage ? (
              <RootButton expand style1={false} onClick={claimHandle} disabled={!code}>
                {t('components.identity.confirmModal.orderRetry', 'Try Again')}
              </RootButton>
            ) : (
              <RootButton
                expand
                style1={false}
                onClick={claimHandle}
                disabled={!code || loadingNewCode}
              >
                {t('buttons.confirm', 'Confirm')}
              </RootButton>
            )}
          </Stack>
        </>
      )}
    </Stack>
  )
}

export default ConfirmFreeUsername
